import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Custom Hooks
import { usePatientContext } from "../classes/contexts/PatientContext";
import { usePlanContext } from "../classes/contexts/PlanContext";
import { parseSortableDate } from "../classes/utils/DateUtils";
import { convertKeys } from "../classes/utils/MedicalImagesUtils";
import { GET_ANNOTATIONS_LIST_BY_IDS_QUERY } from "../queries/annotationQueries";
import { useAnnotations } from "./queries/useAnnotations";
import { useDigitalTwinData } from "./queries/useDigitalTwinData";
import { useInstances } from "./queries/useInstance";
import { useMeasurementConfigs } from "./queries/useMeasurementConfigs";
import { usePlans } from "./queries/usePlans";
import { useSurgicalActs } from "./queries/useSurgicalActs";
import { useToolConfigs } from "./queries/useToolConfigs";
import useFileStorage from "./useFileStorage";
import { useRouteState } from "./useRouteState";

const initialLoadingState = {
  patientDetails: false,
  instances: false,
  studies: false,
  annotations: false,
  measurementConfigs: false,
  customMeasurementConfigs: false,
  toolConfigs: false,
  surgicalActs: false,
  plan: false,
  plans: false,
};

// Initial data bundle structure
const initialDataBundle = {
  // Patient & Study
  patientDetails: null,
  spinesimStudyId: null,
  studiesList: null,

  // Instances & Annotations
  instances: null,
  annotations: null,
  organizedAnnotations: null,

  // Configs
  measurementConfigsFrontEnd: null,
  measurementConfigsBackEnd: null,
  customMeasurementConfigsFrontEnd: null,
  customMeasurementConfigsBackEnd: null,
  toolConfigsFrontEnd: null,
  toolConfigsBackEnd: null,

  // Plans
  allPlans: null,
  currentPlan: null,
  comparedPlan: null,

  // Current Plan Surgical Data
  surgicalActs: null,
  globalAlignmentAct: null,

  // Compared Plan Surgical Data
  comparedSurgicalActs: null,
  comparedGlobalAlignmentAct: null,
};

export const useDataBundle = () => {
  const routeState = useRouteState();
  const twinId = routeState.params.twinId;
  const patientId = routeState.params.patientId;

  const { getMedicalImagePath } = useFileStorage();
  const { patientData } = usePatientContext();

  // State management
  const [loadingState, setLoadingState] = useState(initialLoadingState);
  const [dataBundle, setDataBundle] = useState(initialDataBundle);
  const [constructedImageData, setConstructedImageData] = useState(null);
  const [imageDataConstructed, setImageDataConstructed] = useState(false);
  const [checkReloadToken, setCheckReloadToken] = useState(0);
  const previousAnnotationsRef = useRef(null);
  const previousPatientBirthdayRef = useRef(null);

  const digitalTwinIds = useMemo(() => {
    return twinId ? [twinId] : [];
  }, [twinId]);

  // Context setting
  const { currentPlanId, updateCurrentPlanId, comparedPlanId } =
    usePlanContext();

  // Data fetching hooks
  const {
    digitalTwins: selectedTwinAsList,
    instances: instancesMap,
    loading: digitalTwinLoading,
    refreshDigitalTwins,
  } = useDigitalTwinData(digitalTwinIds);

  const {
    instances,
    originalAnnotationsIds,
    loading: instancesLoading,
    error: instancesError,
    refreshInstances,
  } = useInstances(instancesMap);

  const { filteredOriginalAnnotations, organizedOriginalAnnotations } =
    useAnnotations(originalAnnotationsIds);

  // Lazy query for fetching annotations with fresh IDs during rerun
  const [getAnnotations] = useLazyQuery(GET_ANNOTATIONS_LIST_BY_IDS_QUERY, {
    fetchPolicy: "network-only",
  });

  // Helper functions for organizing and filtering annotations (from useAnnotations)
  const organizeAnnotations = useCallback((ids, annotationsArray) => {
    return Object.keys(ids).reduce((acc, instanceId) => {
      if (!ids[instanceId]) {
        return acc;
      }
      acc[instanceId] = ids[instanceId]
        .map(id => annotationsArray.find(annotation => annotation?._id === id))
        .filter(annotation => annotation !== undefined);
      return acc;
    }, {});
  }, []);

  const removeTypename = useCallback(obj => {
    if (Array.isArray(obj)) {
      return obj.map(removeTypename);
    } else if (obj !== null && typeof obj === "object") {
      const newObj = {};
      for (const key in obj) {
        if (key !== "__typename") {
          newObj[key] = removeTypename(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  }, []);

  const getFilteredAnnotations = useCallback(
    annotationsData => {
      const result = {};
      for (const instanceId in annotationsData) {
        if (annotationsData.hasOwnProperty(instanceId)) {
          if (!result[instanceId]) {
            result[instanceId] = [];
          }
          annotationsData[instanceId].forEach(annotation => {
            if (annotation) {
              const { __typename, ...cleanAnnotation } = annotation;
              result[instanceId].push(removeTypename(cleanAnnotation));
            }
          });
        }
      }
      return result;
    },
    [removeTypename]
  );

  const computeAgeAtDate = useCallback((birthday, targetDate) => {
    if (!birthday || !targetDate) {
      return null;
    }

    const birth = parseSortableDate(birthday) || new Date(birthday);
    const imagingDate = parseSortableDate(targetDate) || new Date(targetDate);

    if (isNaN(birth) || isNaN(imagingDate)) {
      return null;
    }

    let age = imagingDate.getFullYear() - birth.getFullYear();
    const monthDiff = imagingDate.getMonth() - birth.getMonth();
    const dayDiff = imagingDate.getDate() - birth.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age -= 1;
    }

    return age >= 0 ? age : null;
  }, []);

  const buildSubjectDetailsForInstance = useCallback(
    instance => {
      if (!instance) {
        return null;
      }

      const age =
        computeAgeAtDate(patientData?.birthday, instance.creation_date) ?? null;

      const functional_handplate = Number.isFinite(
        Number(instance.functional_handplate)
      )
        ? Number(instance.functional_handplate)
        : null;

      return {
        age,
        functional_handplate,
      };
    },
    [computeAgeAtDate, patientData?.birthday]
  );

  const primarySubjectInstance = useMemo(() => {
    if (!instances || Object.keys(instances).length === 0) {
      return null;
    }

    return (
      instances.sagittal_dx_instance_id ||
      instances.coronal_dx_instance_id ||
      instances.ct_instance_id ||
      Object.values(instances)[0]
    );
  }, [instances]);

  const primarySubjectDetails = useMemo(() => {
    return buildSubjectDetailsForInstance(primarySubjectInstance);
  }, [buildSubjectDetailsForInstance, primarySubjectInstance]);

  const selectedDigitalTwin = useMemo(() => {
    return selectedTwinAsList && selectedTwinAsList.length > 0
      ? selectedTwinAsList[0]
      : null;
  }, [selectedTwinAsList]);

  const planIds = useMemo(() => {
    return selectedDigitalTwin?.plans || [];
  }, [selectedDigitalTwin]);

  const {
    plans: allPlans,
    isAddingPlan,
    createPlan: createPlanBase,
    refetchPlans,
  } = usePlans(planIds, twinId);

  const createPlan = useCallback(async () => {
    const newPlanId = await createPlanBase();
    if (newPlanId) {
      setTimeout(() => {
        refreshDigitalTwins();
      }, 200);
    }
    return newPlanId;
  }, [createPlanBase, refreshDigitalTwins]);

  const currentPlan = useMemo(() => {
    if (!allPlans?.length) {
      return null;
    }

    if (!currentPlanId) {
      return null;
    }

    const foundPlan = allPlans.find(plan => plan._id === currentPlanId);
    return foundPlan || null;
  }, [currentPlanId, allPlans]);

  const comparedPlan = useMemo(() => {
    if (!allPlans?.length) {
      return null;
    }

    if (!comparedPlanId) {
      return null;
    }

    const foundPlan = allPlans.find(plan => plan._id === comparedPlanId);
    return foundPlan || null;
  }, [comparedPlanId, allPlans]);

  const {
    surgicalActs,
    globalAlignmentAct,
    loading: surgicalActsLoading,
    refetch: refetchSurgicalActs,
  } = useSurgicalActs(currentPlan?.surgical_parameters || []);

  const {
    surgicalActs: comparedSurgicalActs,
    globalAlignmentAct: comparedGlobalAlignmentAct,
    loading: comparedSurgicalActsLoading,
    refetch: refetchComparedSurgicalActs,
  } = useSurgicalActs(comparedPlan?.surgical_parameters || []);

  //region Configs

  const {
    measurementConfigsFrontEnd,
    measurementConfigsBackEnd,
    customMeasurementConfigsFrontEnd,
    customMeasurementConfigsBackEnd,
    refreshCustomMeasurementConfigs,
  } = useMeasurementConfigs(selectedDigitalTwin);

  const { toolConfigsBackEnd, toolConfigsFrontEnd } = useToolConfigs();

  const measurementRefresh = useCallback(() => {
    refreshDigitalTwins();
    refreshCustomMeasurementConfigs();
  }, [refreshDigitalTwins, refreshCustomMeasurementConfigs]);

  const validationRefresh = useCallback(async () => {
    // IMPORTANT: Fetch instances FIRST to get the NEW annotation IDs after rerun
    // Cannot fetch in parallel because annotations depend on the fresh instance data
    const instancesResult = await refreshInstances();
    const instances = instancesResult?.instances || {};
    const freshAnnotationIds = instancesResult?.annotations || {};

    if (Object.keys(instances).length === 0) {
      return null;
    }

    // Step 2: Fetch annotations using the FRESH annotation IDs from instances
    // Extract all annotation IDs from the fresh instances
    const allFreshAnnotationIds = Object.values(freshAnnotationIds).flat();

    if (allFreshAnnotationIds.length === 0) {
      return null;
    }

    const annotationsResult = await getAnnotations({
      variables: { ids: allFreshAnnotationIds },
      fetchPolicy: "network-only",
    });

    if (!annotationsResult.data?.annotationsByIds) {
      return null;
    }

    const organized = organizeAnnotations(
      freshAnnotationIds,
      annotationsResult.data.annotationsByIds
    );
    const annotations = getFilteredAnnotations(organized);

    if (
      Object.keys(instances).length === 0 ||
      Object.keys(annotations).length === 0
    ) {
      return null;
    }

    const instancesArray = Object.values(instances);

    const imageDataPromises = instancesArray.map(async instance => {
      const keys = convertKeys(instance.modality, instance.orientation);

      let path;
      try {
        path = await getMedicalImagePath(
          instance.tenant_id,
          patientId,
          instance._id,
          instance.file_name
        );
      } catch (error) {
        path = `/spinesim/data/default/${instance._id}/${instance.file_name}`;
      }

      const instanceAnnotations = annotations[instance._id] || [];

      return {
        key: keys.key,
        data: {
          path,
          annotations: instanceAnnotations,
          subject_details:
            buildSubjectDetailsForInstance(instance) || primarySubjectDetails,
          measurementConfigs:
            dataBundle.measurementConfigsBackEnd[keys.standard] || [],
          customMeasurementConfigs:
            dataBundle.customMeasurementConfigsBackEnd[keys.custom] || [],
          toolConfigs: dataBundle.toolConfigsBackEnd[keys.standard] || [],
        },
      };
    });

    const resolvedImageData = await Promise.all(imageDataPromises);
    const freshImageData = resolvedImageData.reduce((acc, { key, data }) => {
      acc[key] = data;
      return acc;
    }, {});

    setConstructedImageData(freshImageData);
    setImageDataConstructed(true);

    return freshImageData;
  }, [
    refreshInstances,
    getAnnotations,
    organizeAnnotations,
    getFilteredAnnotations,
    dataBundle,
    getMedicalImagePath,
    patientId,
    primarySubjectDetails,
    buildSubjectDetailsForInstance,
  ]);

  useEffect(() => {
    if (!dataBundle.annotations) {
      return;
    }

    if (previousAnnotationsRef.current !== dataBundle.annotations) {
      previousAnnotationsRef.current = dataBundle.annotations;

      if (imageDataConstructed) {
        setConstructedImageData(null);
        setImageDataConstructed(false);
      }
    }
  }, [dataBundle.annotations, imageDataConstructed]);

  useEffect(() => {
    if (!patientData) {
      return;
    }

    const currentBirthday = patientData?.birthday || null;

    if (previousPatientBirthdayRef.current === currentBirthday) {
      return;
    }

    previousPatientBirthdayRef.current = currentBirthday;

    if (imageDataConstructed) {
      setConstructedImageData(null);
      setImageDataConstructed(false);
    }
  }, [patientData, imageDataConstructed]);

  const surgicalActsRefresh = useCallback(() => {
    if (refetchSurgicalActs) {
      return refetchSurgicalActs();
    }
  }, [refetchSurgicalActs]);

  const plansRefresh = useCallback(() => {
    if (refetchPlans) {
      return refetchPlans();
    }
  }, [refetchPlans]);

  const triggerCheckReload = useCallback(() => {
    setCheckReloadToken(prev => prev + 1);
  }, []);

  //region SETUP

  const isBasicDataLoaded = useMemo(() => {
    return (
      loadingState.instances &&
      loadingState.annotations &&
      loadingState.measurementConfigs &&
      loadingState.toolConfigs
    );
  }, [loadingState]);

  const isCurrentDataLoaded = useMemo(() => {
    return (
      isBasicDataLoaded &&
      loadingState.plan &&
      loadingState.surgicalActs &&
      loadingState.plans
    );
  }, [
    isBasicDataLoaded,
    loadingState.plan,
    loadingState.surgicalActs,
    loadingState.plans,
  ]);

  //region DataBundle Sync Effects

  // Instances & Annotations
  useEffect(() => {
    if (instances && Object.keys(instances).length > 0) {
      setDataBundle(prev => ({
        ...prev,
        instances,
      }));

      setLoadingState(prev => ({
        ...prev,
        instances: true,
      }));
    }
  }, [instances]);

  useEffect(() => {
    if (filteredOriginalAnnotations && organizedOriginalAnnotations) {
      setDataBundle(prev => ({
        ...prev,
        annotations: filteredOriginalAnnotations,
        organizedAnnotations: organizedOriginalAnnotations,
      }));

      setLoadingState(prev => ({
        ...prev,
        annotations: true,
      }));
    }
  }, [filteredOriginalAnnotations, organizedOriginalAnnotations]);

  // Configs
  useEffect(() => {
    if (measurementConfigsBackEnd && measurementConfigsFrontEnd) {
      setDataBundle(prev => ({
        ...prev,
        measurementConfigsBackEnd,
        measurementConfigsFrontEnd,
      }));

      setLoadingState(prev => ({
        ...prev,
        measurementConfigs: true,
      }));
    }
  }, [measurementConfigsBackEnd, measurementConfigsFrontEnd]);

  useEffect(() => {
    if (customMeasurementConfigsBackEnd && customMeasurementConfigsFrontEnd) {
      setDataBundle(prev => ({
        ...prev,
        customMeasurementConfigsBackEnd,
        customMeasurementConfigsFrontEnd,
      }));

      setLoadingState(prev => ({
        ...prev,
        customMeasurementConfigs: true,
      }));
    }
  }, [customMeasurementConfigsBackEnd, customMeasurementConfigsFrontEnd]);

  useEffect(() => {
    if (toolConfigsBackEnd && toolConfigsFrontEnd) {
      setDataBundle(prev => ({
        ...prev,
        toolConfigsBackEnd,
        toolConfigsFrontEnd,
      }));

      setLoadingState(prev => ({
        ...prev,
        toolConfigs: true,
      }));
    }
  }, [toolConfigsBackEnd, toolConfigsFrontEnd]);

  // Plans
  useEffect(() => {
    if (allPlans !== null) {
      setDataBundle(prev => ({
        ...prev,
        allPlans,
      }));

      setLoadingState(prev => ({
        ...prev,
        plans: true,
      }));
    }
  }, [allPlans]);

  // Current Plan & Surgical Data
  useEffect(() => {
    if (currentPlan && surgicalActs !== null && globalAlignmentAct !== null) {
      setDataBundle(prev => ({
        ...prev,
        currentPlan,
        surgicalActs,
        globalAlignmentAct,
      }));

      setLoadingState(prev => ({
        ...prev,
        plan: true,
        surgicalActs: true,
      }));
    }
  }, [currentPlan, surgicalActs, globalAlignmentAct]);

  // Compared Plan & Surgical Data
  useEffect(() => {
    setDataBundle(prev => ({
      ...prev,
      comparedPlan,
      comparedSurgicalActs,
      comparedGlobalAlignmentAct,
    }));
  }, [comparedPlan, comparedSurgicalActs, comparedGlobalAlignmentAct]);

  //endregion

  const areAllInstancesValidated = useMemo(() => {
    if (!instances || Object.keys(instances).length === 0) {
      return false;
    }

    const allInstances = Object.values(instances);
    const validationStatus = allInstances.every(
      instance => instance.validated === true
    );

    return validationStatus;
  }, [instances]);

  const areDxInstancesValidated = useMemo(() => {
    if (!instances || Object.keys(instances).length === 0) {
      return false;
    }

    // Check all DX instances that are actually linked to the twin
    const dxInstances = [
      instances.sagittal_dx_instance_id,
      instances.coronal_dx_instance_id,
    ].filter(inst => inst != null);

    if (dxInstances.length === 0) {
      return false;
    }

    return dxInstances.every(instance => instance.validated === true);
  }, [instances]);

  // Align requires sagittal DX specifically
  const isSagittalDxValidated = useMemo(() => {
    if (!instances || Object.keys(instances).length === 0) {
      return false;
    }

    const sagittalInstance = instances.sagittal_dx_instance_id;
    if (!sagittalInstance) {
      return false;
    }

    return sagittalInstance.validated === true;
  }, [instances]);

  const areDrrInstancesValidated = useMemo(() => {
    if (!instances || Object.keys(instances).length === 0) {
      return false;
    }
    const ctInstance = instances.ct_instance_id;
    if (!ctInstance) {
      return false;
    }

    const validationStatus = ctInstance.validated === true;

    return validationStatus;
  }, [instances]);

  useEffect(() => {
    const shouldConstructImageData =
      isBasicDataLoaded &&
      dataBundle.instances &&
      !imageDataConstructed;

    if (shouldConstructImageData) {
      setImageDataConstructed(true);

      const buildImageData = async () => {
        try {
          const instances = dataBundle.instances;
          const instancesArray = Object.values(instances);

          const imageDataPromises = instancesArray.map(async instance => {
            const keys = convertKeys(instance.modality, instance.orientation);

            let path;
            try {
              path = await getMedicalImagePath(
                instance.tenant_id,
                patientId,
                instance._id,
                instance.file_name
              );
            } catch (error) {
              path = `/spinesim/data/default/${instance._id}/${instance.file_name}`;
            }

            // Use filteredAnnotations (without __typename) instead of organizedAnnotations
            const instanceAnnotations =
              dataBundle.annotations[instance._id] || [];

            return {
              key: keys.key,
              data: {
                path,
                annotations: instanceAnnotations,
                subject_details:
                  buildSubjectDetailsForInstance(instance) ||
                  primarySubjectDetails,
                measurementConfigs:
                  dataBundle.measurementConfigsBackEnd[keys.standard] || [],
                customMeasurementConfigs:
                  dataBundle.customMeasurementConfigsBackEnd[keys.custom] || [],
                toolConfigs: dataBundle.toolConfigsBackEnd[keys.standard] || [],
              },
            };
          });

          const resolvedImageData = await Promise.all(imageDataPromises);
          const imageData = resolvedImageData.reduce((acc, { key, data }) => {
            acc[key] = data;
            return acc;
          }, {});

          if (imageData) {
            setConstructedImageData(imageData);

            //
            Object.entries(imageData).forEach(([key, data]) => {});
          }
        } catch (error) {
          setImageDataConstructed(false);
        }
      };

      buildImageData();
    }
  }, [
    isBasicDataLoaded,
    dataBundle.instances,
    dataBundle.annotations,
    dataBundle.measurementConfigsBackEnd,
    dataBundle.customMeasurementConfigsBackEnd,
    dataBundle.toolConfigsBackEnd,
    imageDataConstructed,
    getMedicalImagePath,
    patientId,
    dataBundle.organizedAnnotations,
    isCurrentDataLoaded,
    primarySubjectDetails,
    buildSubjectDetailsForInstance,
  ]);

  // Computed loading states
  const isLoading =
    digitalTwinLoading ||
    instancesLoading ||
    surgicalActsLoading ||
    comparedSurgicalActsLoading ||
    isAddingPlan;

  const hasError = instancesError;

  return {
    // Data bundle and constructed data
    dataBundle,
    constructedImageData,

    // Loading states
    loadingState,
    isCurrentDataLoaded,
    isLoading,
    hasError,
    isPatientDetailsLoaded: loadingState.patientDetails,
    isInstancesLoaded: loadingState.instances,
    isStudiesLoaded: loadingState.studies,
    isAnnotationsLoaded: loadingState.annotations,
    isMeasurementConfigsLoaded: loadingState.measurementConfigs,
    isCustomMeasurementConfigsLoaded: loadingState.customMeasurementConfigs,
    isToolConfigsLoaded: loadingState.toolConfigs,
    isSurgicalActsLoaded: loadingState.surgicalActs,
    isPlanLoaded: loadingState.plan,
    isPlansLoaded: loadingState.plans,

    // Refresh functions
    measurementRefresh,
    validationRefresh,
    surgicalActsRefresh,
    plansRefresh,

    // Digital Twin & Instances
    selectedTwin: selectedTwinAsList,
    instances,
    areAllInstancesValidated,
    areDxInstancesValidated,
    isSagittalDxValidated,
    areDrrInstancesValidated,

    // Annotations
    originalAnnotationsIds,
    filteredOriginalAnnotations,
    organizedOriginalAnnotations,

    // Configs
    measurementConfigsFrontEnd,
    measurementConfigsBackEnd,
    customMeasurementConfigsFrontEnd,
    customMeasurementConfigsBackEnd,
    toolConfigsFrontEnd,
    toolConfigsBackEnd,

    // Plans
    allPlans,
    currentPlan,
    currentPlanId,
    comparedPlan,
    comparedPlanId,
    createPlan,
    updateCurrentPlanId,

    // Current Plan Surgical Data
    surgicalActs,
    globalAlignmentAct,

    // Compared Plan Surgical Data
    comparedSurgicalActs,
    comparedGlobalAlignmentAct,

    // Misc
    checkReloadToken,
    triggerCheckReload,
    refreshDigitalTwins,
  };
};
